/** Trip history: local log merged with the server copy, per signed-in driver. */

import { listTrips, deleteTrip } from '../core/db';
import { fetchTrips } from '../auth/api';
import type { Trip } from '../core/types';
import { formatDateTime, formatDistance, formatDuration } from '../core/format';
import { store } from '../core/store';
import { t } from '../core/i18n';

export class TripsPanel {
  readonly el: HTMLElement;

  constructor(
    parent: HTMLElement,
    private onClose: () => void,
    private onNavigateAgain: (trip: Trip) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--trips';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  async open(): Promise<void> {
    this.el.hidden = false;
    this.renderList(null);
    const local = await listTrips();
    let merged = local;
    try {
      const server = (await fetchTrips()).trips;
      const seen = new Set(local.map((t) => t.id));
      merged = [...local, ...server.filter((t) => !seen.has(t.id))].sort(
        (a, b) => b.startedAt - a.startedAt,
      );
    } catch {
      /* offline: local only */
    }
    if (!this.el.hidden) this.renderList(merged);
  }

  close(): void {
    this.el.hidden = true;
  }

  private renderList(trips: Trip[] | null): void {
    const units = store.get().settings.units;
    const body =
      trips === null
        ? `<div class="panel__hint">${t('trips.loading')}</div>`
        : trips.length === 0
          ? `<div class="panel__hint">${t('trips.empty')}</div>`
          : trips
              .map(
                (tr, i) => `
        <div class="trip" data-i="${i}">
          <div class="trip__head">
            <strong>${escapeHtml(tr.destinationName)}</strong>
            <span>${formatDateTime(tr.startedAt)}</span>
          </div>
          <div class="trip__meta">
            ${formatDistance(tr.distance, units)} · ${formatDuration(tr.duration)} · ${escapeHtml(tr.vehicleId)}
          </div>
          <div class="trip__actions">
            <button class="btn btn--small btn--ghost" data-again="${i}">${t('trips.again')}</button>
            <button class="btn btn--small btn--ghost" data-del="${i}">${t('trips.delete')}</button>
          </div>
        </div>`,
              )
              .join('');

    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2>${t('trips.title')}</h2>
      </div>
      <div class="panel__scroll">${body}</div>`;

    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    if (!trips) return;
    this.el.querySelectorAll<HTMLButtonElement>('[data-again]').forEach((b) =>
      b.addEventListener('click', () => this.onNavigateAgain(trips[Number(b.dataset.again)])),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((b) =>
      b.addEventListener('click', async () => {
        const tr = trips[Number(b.dataset.del)];
        await deleteTrip(tr.id);
        void this.open();
      }),
    );
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
